import React from "react";
import { Alert, CircularProgress } from "@mui/material";

// Result of the server-side destination test (Lambda srecPanelTestDestination,
// POST /destination-test). null while the test is running.
type Props = {
  result: any;
};

function formatRequest(request: any): string {
  if (!request || !request.url) return "";
  const lines = [`${request.method} ${request.url}`];
  if (request.headers) {
    Object.keys(request.headers).forEach((name) => lines.push(`${name}: ${request.headers[name]}`));
  }
  if (request.body) lines.push("", request.body);
  return lines.join("\n");
}

const DestinationTestResult = (props: Props) => {
  const r = props.result;
  if (!r) {
    return (
      <section className="destination-test">
        <div className="destination-test__waiting">
          <CircularProgress size={20} />
          <span>Sending a test lead from our server...</span>
        </div>
      </section>
    );
  }

  const answered = typeof r.status === "number";
  const statusText = answered ? `HTTP ${r.status}${r.statusText ? " " + r.statusText : ""}` : "";
  const errorText = r.error ? r.error.message : "unknown error";
  // r.sent: true = our server reached the endpoint, false = nothing was sent,
  // null/undefined = unknown (our own API did not return a result).
  let severity: "success" | "warning" | "error" = "error";
  let headline = "";
  if (r.ok) {
    severity = "success";
    headline = `Your endpoint answered ${statusText} in ${r.ms} ms.`;
  } else if (answered) {
    headline = `Your endpoint answered ${statusText} in ${r.ms} ms. Real leads count as failed when the answer is not 2xx.`;
  } else if (r.sent === true) {
    headline = `The test was sent, but no answer came back: ${errorText}`;
  } else if (r.sent === false) {
    headline = `Not sent: ${errorText}`;
  } else {
    severity = "warning";
    headline = `${errorText} The test lead may still have reached your endpoint.`;
  }
  const notes: string[] = Array.isArray(r.notes) ? r.notes : [];
  const requestLabel = r.sent === true ? "Request sent" : r.sent === false ? "Request (not sent)" : "Request";
  const requestText = formatRequest(r.request);

  return (
    <section className="destination-test">
      <h3 className="destination-test__title">Server-side test</h3>
      <Alert severity={severity}>{headline}</Alert>
      {r.sent === true ? (
        <p className="destination-test__meta">
          Sent from our server (not your browser){r.sentAt ? ` at ${new Date(r.sentAt).toLocaleString()}` : ""}, built the
          same way as a real lead, with sample values (email {r.sample ? r.sample.email : ""}, IP {r.sample ? r.sample.ip : ""}).
        </p>
      ) : null}
      {notes.map((note, i) => (
        <Alert key={i} severity="warning" className="destination-test__note">{note}</Alert>
      ))}
      {r.redirects && r.redirects.length > 0 ? (
        <>
          <div className="destination-test__label">Redirects</div>
          <pre className="customized-dialog-box__code-section destination-test__pre">
            {r.redirects.map((x: any) => `${x.status} -> ${x.location}`).join("\n")}
          </pre>
        </>
      ) : null}
      {r.response ? (
        <>
          <div className="destination-test__label">
            Response{r.response.contentType ? ` (${r.response.contentType})` : ""}{r.response.truncated ? ", first 2,000 characters" : ""}
          </div>
          <pre className="customized-dialog-box__code-section destination-test__pre">{r.response.body || "(empty)"}</pre>
        </>
      ) : null}
      {requestText ? (
        <>
          <div className="destination-test__label">{requestLabel}</div>
          <pre className="customized-dialog-box__code-section destination-test__pre">{requestText}</pre>
        </>
      ) : null}
    </section>
  );
};

export default DestinationTestResult;
